import type { WorkItem } from '@/api/work';
import { dealTabPath, type DealTabId } from '@/features/pipeline/dealTabs';
import { recordPath } from '@/ui';

import type { RowDraft } from './planTypes';
import { withNote } from './rowText';

/**
 * The deal an item belongs to, whether the item is the deal or something on it.
 *
 * @param item the item
 * @returns the deal's id, or null when the item belongs to no deal
 */
export function dealIdOf(item: WorkItem): string | null {
  return item.dealId ?? (item.recordType === 'DEAL' ? item.recordId : null);
}

/**
 * Where an item's deal is worked on, at the tab that does the step.
 *
 * @param item the item
 * @param tab the deal hub tab
 * @param fallback where to go when the item belongs to no deal
 * @returns an app path
 */
export function dealStep(item: WorkItem, tab: DealTabId, fallback: string): string {
  const dealId = dealIdOf(item);
  return dealId === null ? fallback : dealTabPath(dealId, tab);
}

/**
 * Where an item opens when its section has no more specific step.
 *
 * @param item the item
 * @returns the record's own page, or Today when it has none
 */
export function openPathOf(item: WorkItem): string {
  switch (item.recordType) {
    case 'LEAD':
      return recordPath('lead', item.recordId);
    case 'PROPOSAL':
      return recordPath('proposal', item.recordId);
    case 'JOB':
      return recordPath('job', item.recordId);
    case 'INVOICE':
      return recordPath('invoice', item.recordId);
    default:
      return dealStep(item, 'overview', '/');
  }
}

/**
 * A row for an item a section was not expecting, so a newer server never leaves a gap.
 *
 * @param item the item
 * @returns a plain row that opens the record
 */
export function fallbackRow(item: WorkItem): RowDraft {
  return {
    subject: item.title,
    predicate: withNote('waiting for you', 'noting', item.detail),
    action: { label: 'Open', to: openPathOf(item) },
  };
}
